import { useState } from 'react';

type GuestRow = {
  rowKey: string;
  guest: Participant;
};

type GuestChanges = Pick<Participant, 'name' | 'phoneNumber'>;

type UseGuestList = {
  rows: GuestRow[];
  guests: Participant[];
  addGuest: (guest: GuestChanges) => void;
  updateGuest: (rowKey: string, changes: GuestChanges) => void;
  removeGuest: (rowKey: string) => void;
  replaceGuests: (guests: Participant[]) => void;
};

// Guests have no unique natural key: new ones have no id yet, and name + phone can repeat.
let lastRowKey = 0;

const toGuestRow = (guest: Participant): GuestRow => {
  lastRowKey += 1;
  return { rowKey: `guest-row-${lastRowKey}`, guest };
};

export const useGuestList = (initialGuests: Participant[]): UseGuestList => {
  const [rows, setRows] = useState<GuestRow[]>(() =>
    initialGuests.map(toGuestRow)
  );

  const addGuest = (guest: GuestChanges) => {
    const row = toGuestRow(guest);
    setRows((prev) => [...prev, row]);
  };

  const updateGuest = (rowKey: string, changes: GuestChanges) => {
    setRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? { rowKey, guest: { ...row.guest, ...changes } }
          : row
      )
    );
  };

  const removeGuest = (rowKey: string) => {
    setRows((prev) => prev.filter((row) => row.rowKey !== rowKey));
  };

  const replaceGuests = (guests: Participant[]) => {
    setRows(guests.map(toGuestRow));
  };

  return {
    rows,
    guests: rows.map(({ guest }) => guest),
    addGuest,
    updateGuest,
    removeGuest,
    replaceGuests,
  };
};
